<!-- 
  Science Dashboard for Bird Detection Research
  
  Required npm packages:
  - npm install leaflet.heat
  
  This dashboard provides scientific analysis of bird detection data with:
  - Species filtering with 6000+ species
  - Date range selection
  - Score threshold filtering (client-side)
  - Time series visualizations
  - Geographic distribution (circles and heatmap)
  - Device breakdown statistics
  - Stop query capability for long-running queries
  - CSV export with score binning
-->
<template>

   

    <v-container v-if="user != ''" fluid>


 <!-- Password Dialog -->
 <v-dialog v-model="passwordDialog" max-width="400">
      <v-card>
        <v-card-title>Science Dashboard Password</v-card-title>
        <v-card-text>
          <v-text-field
              v-model="password"
              label="Password"
              type="password"
              @keyup.enter="checkPassword"
              :error="incorrectPassword"
              :error-messages="incorrectPassword ? 'Incorrect password' : ''"
              tabindex="0"
            ></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="checkPassword">Submit</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Your existing content, wrapped with v-if -->
    <div v-if="isAuthenticated">


      <v-card dark class="mx-auto my-2">
        <v-card-title>
          <v-icon left>mdi-flask-outline</v-icon>
          Scientific Research Dashboard
        </v-card-title>
        
        <!-- Filter Controls -->
        <v-card-text>
          <v-row>
            <!-- Species Selection -->
            <v-col cols="12" md="4">
              <v-autocomplete
                v-model="selectedSpecies"
                :items="speciesList"
                label="Select Bird Species"
                item-text="display"
                item-value="cn"
                :loading="loadingSpecies"
                :filter="customSpeciesFilter"
                clearable
                dense
                outlined
                prepend-inner-icon="mdi-bird"
              >
                <template v-slot:item="{ item }">
                  <v-list-item-content>
                    <v-list-item-title>{{ item.cn }}</v-list-item-title>
                    <v-list-item-subtitle>{{ item.sn }} ({{ item.spCode }})</v-list-item-subtitle>
                  </v-list-item-content>
                </template>
              </v-autocomplete>
            </v-col>
  
            <!-- Date Range Selection -->
            <v-col cols="12" md="3">
              <v-menu
                v-model="startDateMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="startDate"
                    label="Start Date"
                    prepend-inner-icon="mdi-calendar"
                    v-bind="attrs"
                    v-on="on"
                    dense
                    outlined
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="startDate"
                  @input="startDateMenu = false"
                ></v-date-picker>
              </v-menu>
            </v-col>
  
            <v-col cols="12" md="3">
              <v-menu
                v-model="endDateMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="endDate"
                    label="End Date"
                    prepend-inner-icon="mdi-calendar"
                    v-bind="attrs"
                    v-on="on"
                    dense
                    outlined
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="endDate"
                  @input="endDateMenu = false"
                ></v-date-picker>
              </v-menu>
            </v-col>
  
            <!-- Quick Date Range Buttons -->
            <v-col cols="12" md="2">
              <v-btn-toggle v-model="quickRange" mandatory>
                <v-btn small value="7d">7D</v-btn>
                <v-btn small value="30d">30D</v-btn>
                <v-btn small value="90d">90D</v-btn>
              </v-btn-toggle>
            </v-col>
          </v-row>
  
          <v-row>
            <!-- Query Button -->
            <v-col cols="12" class="d-flex align-center justify-center">
              <v-btn
                v-if="!querying"
                color="primary"
                @click="queryData"
                :disabled="!selectedSpecies || !startDate || !endDate"
                large
              >
                <v-icon left>mdi-database-search</v-icon>
                Query Database
              </v-btn>
              
              <v-btn
                v-if="querying"
                color="error"
                @click="stopQuery"
                large
              >
                <v-icon left>mdi-stop</v-icon>
                Stop Query
              </v-btn>
              

            </v-col>
          </v-row>
  
          <!-- Progress Bar -->
          <v-progress-linear
            v-if="querying"
            :value="queryProgress"
            height="25"
            color="primary"
            striped
          >
            <template v-slot:default="{ value }">
              <strong>{{ Math.ceil(value) }}%</strong>
              <span class="ml-2">{{ progressMessage }}</span>
            </template>
          </v-progress-linear>
        </v-card-text>
      </v-card>
  
      <!-- Results Section -->
      <v-row v-if="hasQueried && !querying">
        <!-- Time Series Chart -->
        <v-col cols="12">
          <v-card dark>
            <v-card-title>
              Detection Time Series
              <v-chip
                v-if="selectedRegions.length > 0"
                color="grey darken-2"
                text-color="white"
                small
                class="ml-2"
              >
                <v-icon left small>mdi-map-marker-multiple</v-icon>
                {{ selectedRegions.length }} regions
              </v-chip>
              <v-spacer></v-spacer>
              <v-btn-toggle v-model="timeGrouping" mandatory dense>
                <v-btn small value="hourly">Hourly</v-btn>
                <v-btn small value="daily">Daily</v-btn>
                <v-btn small value="weekly">Weekly</v-btn>
              </v-btn-toggle>
            </v-card-title>
            <v-card-text>
              <div v-if="selectedRegions.length > 0 && !hasDataInAnyRegion" class="text-center grey--text">
                No detections found in any selected region
              </div>
              <line-chart
                v-else
                :key="`chart-${selectedRegions.length}-${selectedRegions.map(r => r.id).join('-')}`"
                :data="formattedChartData"
                :height="300"
                :colors="chartColors"
                :library="chartOptions"
              ></line-chart>
            </v-card-text>
          </v-card>
        </v-col>
  
        <!-- Map Visualization -->
        <v-col cols="12">
          <v-card dark>
            <v-card-title>
              Geographic Distribution
              <v-spacer></v-spacer>
              <v-btn
                v-if="mapView === 'circles'"
                :color="regionSelectionMode ? 'error' : 'primary'"
                @click="toggleRegionSelection"
                small
                class="mr-2"
              >
                <v-icon left small>{{ regionSelectionMode ? 'mdi-close' : 'mdi-selection-ellipse' }}</v-icon>
                {{ regionSelectionMode ? 'Cancel Selection' : 'Select Regions' }}
              </v-btn>
              <v-btn
                v-if="selectedRegions.length > 0"
                color="warning"
                @click="clearRegions"
                small
                class="mr-2"
              >
                <v-icon left small>mdi-delete</v-icon>
                Clear Regions
              </v-btn>
              <v-btn-toggle v-model="mapView" mandatory dense>
                <v-btn small value="circles">Circles</v-btn>
                <v-btn small value="heatmap">Heatmap</v-btn>
                <v-btn small value="animated">Animated</v-btn>
              </v-btn-toggle>
            </v-card-title>
            
            <!-- Filter Controls Panel -->
            <v-card-text class="pb-2">
              <v-expansion-panels v-model="filterPanel" dark>
                <v-expansion-panel>
                  <v-expansion-panel-header>
                    <div>
                      <v-icon left small>mdi-filter</v-icon>
                      Detection Filters
                      <v-chip
                        v-if="totalDetections > 0"
                        color="green"
                        text-color="white"
                        small
                        class="ml-2"
                      >
                        {{ filteredDetections.length + filteredNotInGridDetections.length }} / {{ totalDetections + allNotInGridDetections.length }}
                      </v-chip>
                    </div>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <v-row>
                      <v-col cols="12" md="6">
                        <v-slider
                          v-model="minScore"
                          :max="0.99"
                          :min="0"
                          :step="0.01"
                          label="Minimum Detection Score"
                          thumb-label="always"
                          @change="filterDataByScore"
                          color="primary"
                          class="mt-4"
                        ></v-slider>
                      </v-col>
                      <v-col cols="12" md="6">
                        <v-slider
                          v-model="minDailyDetections"
                          :max="20"
                          :min="1"
                          :step="1"
                          label="Minimum Detections Per Day"
                          thumb-label="always"
                          @change="filterDataByScore"
                          color="primary"
                          class="mt-4"
                          hint="Hide locations with fewer than this many detections on any given day"
                          persistent-hint
                        ></v-slider>
                      </v-col>
                    </v-row>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </v-card-text>
            
            <!-- Animation Controls -->
            <v-card-text v-if="mapView === 'animated'" class="pt-0 pb-2">
              <v-expansion-panels v-model="animationPanel" dark>
                <v-expansion-panel>
                  <v-expansion-panel-header>
                    <div>
                      <v-icon left small>mdi-play-circle</v-icon>
                      Animation Controls
                    </div>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <v-row align="center">
                      <v-col cols="auto">
                        <v-btn
                          icon
                          @click="toggleAnimation"
                          :color="animating ? 'error' : 'primary'"
                        >
                          <v-icon>{{ animating ? 'mdi-pause' : 'mdi-play' }}</v-icon>
                        </v-btn>
                      </v-col>
                      
                      <v-col>
                        <v-slider
                          v-model="currentAnimationDate"
                          :max="animationDates.length - 1"
                          :min="0"
                          :step="1"
                          @change="updateAnimationFrame"
                          :label="currentDateLabel"
                          hide-details
                          :disabled="animating"
                        ></v-slider>
                      </v-col>
                      
                      <v-col cols="auto">
                        <v-select
                          v-model="animationSpeed"
                          :items="[
                            { text: '0.5x', value: 2000 },
                            { text: '1x', value: 1000 },
                            { text: '2x', value: 500 },
                            { text: '4x', value: 250 }
                          ]"
                          label="Speed"
                          dense
                          hide-details
                          style="width: 100px"
                        ></v-select>
                      </v-col>
                    </v-row>
                    <v-row v-if="animationDates.length === 0 && filteredDetections.length > 0">
                      <v-col cols="12" class="text-center">
                        <v-alert type="info" dense>
                          No daily data available for animation
                        </v-alert>
                      </v-col>
                    </v-row>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </v-card-text>
            
            <v-card-text class="pt-0" style="position: relative;">
              <!-- Date overlay for animated view -->
              <div v-if="mapView === 'animated' && currentDateLabel" class="date-overlay">
                <v-chip color="primary" text-color="white" large>
                  <v-icon left>mdi-calendar</v-icon>
                  {{ currentDateLabel }}
                </v-chip>
              </div>
              
              <!-- Region selection instructions -->
              <div v-if="regionSelectionMode" class="region-instructions">
                <v-alert type="info" dense>
                  Click on the map to place region centers. Click again to set radius.
                </v-alert>
              </div>
              
              <!-- Selected regions legend -->
              <div v-if="selectedRegions.length > 0 && !regionSelectionMode" class="regions-legend">
                <v-card dark elevation="2">
                  <v-card-text class="pa-1">
                    <div 
                      v-for="region in selectedRegions"
                      :key="`legend-${region.id}`"
                      class="d-flex align-center"
                    >
                      <v-icon :color="region.color" small>mdi-circle</v-icon>
                      <span class="text-caption ml-1">
                        {{ region.name }}
                        <span class="grey--text">({{ regionDetectionCounts[region.id] || 0 }})</span>
                      </span>
                    </div>
                  </v-card-text>
                </v-card>
              </div>
              
              <l-map
                :zoom="zoom"
                :center="mapCenter"
                style="height: 400px"
                ref="detectionMap"
                @click="onMapClick"
              >
                <l-tile-layer
                  :url="urlTiles"
                  :attribution="attribution"
                />
                
                <!-- Circle markers view -->
                <template v-if="mapView === 'circles'">
                  <!-- Regular detections (blue circles) -->
                  <l-circle
                    v-for="location in locationData"
                    :key="`regular-${location.lat}-${location.lon}`"
                    :lat-lng="[location.lat, location.lon]"
                    :radius="getCircleRadius(location.count, 'regular')"
                    :color="'#2196F3'"
                    :fillColor="'#2196F3'"
                    :fillOpacity="0.6"
                  >
                    <l-popup>
                      <div>
                        <strong>{{ selectedSpecies }}</strong><br>
                        <em>{{ selectedSpeciesData ? selectedSpeciesData.sn : '' }}</em><br>
                        Code: {{ selectedSpeciesData ? selectedSpeciesData.spCode : '' }}<br>
                        <strong style="color: #2196F3">Regular Detection</strong><br>
                        Detections: {{ location.count }}<br>
                        Location: {{ location.lat.toFixed(4) }}, {{ location.lon.toFixed(4) }}<br>
                        Devices: {{ location.devices.join(', ') }}
                      </div>
                    </l-popup>
                  </l-circle>
                  
                  <!-- Not-in-grid detections (red dots) -->
                  <l-circle
                    v-for="location in notInGridLocationData"
                    :key="`notingrid-${location.lat}-${location.lon}`"
                    :lat-lng="[location.lat, location.lon]"
                    :radius="getCircleRadius(location.count, 'notInGrid')"
                    :color="'#f44336'"
                    :fillColor="'#f44336'"
                    :fillOpacity="0.8"
                  >
                    <l-popup>
                      <div>
                        <strong>{{ selectedSpecies }}</strong><br>
                        <em>{{ selectedSpeciesData ? selectedSpeciesData.sn : '' }}</em><br>
                        Code: {{ selectedSpeciesData ? selectedSpeciesData.spCode : '' }}<br>
                        <strong style="color: #f44336">Not-In-Grid Detection</strong><br>
                        Detections: {{ location.count }}<br>
                        Location: {{ location.lat.toFixed(4) }}, {{ location.lon.toFixed(4) }}<br>
                        Devices: {{ location.devices.join(', ') }}
                      </div>
                    </l-popup>
                  </l-circle>
                </template>
  
                <!-- Region circles (shown in all views) -->
                <l-circle
                  v-for="region in selectedRegions"
                  :key="`region-${region.id}-${region.center.lat}-${region.center.lng}-${region.radius}`"
                  :lat-lng="[region.center.lat, region.center.lng]"
                  :radius="region.radius"
                  :color="region.color"
                  :fillColor="region.color"
                  :fillOpacity="0.2"
                  :weight="3"
                >
                  <l-popup>
                    <div>
                      <strong>{{ region.name }}</strong><br>
                      Radius: {{ Math.round(region.radius / 1000) }} km<br>
                      Center: {{ region.center.lat.toFixed(4) }}, {{ region.center.lng.toFixed(4) }}
                    </div>
                  </l-popup>
                </l-circle>
                
                <!-- Heatmap and animated views are rendered programmatically via leaflet.heat plugin -->
              </l-map>
            </v-card-text>
          </v-card>
        </v-col>
  
        <!-- Regions Management Panel -->
        <v-col cols="12" v-if="selectedRegions.length > 0">
          <v-card dark>
            <v-card-title>
              <v-icon left>mdi-map-marker-radius</v-icon>
              Region Management
              <v-spacer></v-spacer>
              <v-btn
                color="warning"
                @click="clearRegions"
                small
              >
                <v-icon left small>mdi-delete-sweep</v-icon>
                Clear All Regions
              </v-btn>
            </v-card-title>
            <v-card-text>
              <v-row>
                <v-col 
                  v-for="region in selectedRegions" 
                  :key="region.id"
                  cols="12" 
                  md="6" 
                  lg="4"
                >
                  <v-card outlined :style="`border-color: ${region.color}`">
                    <v-card-title class="pb-2">
                      <v-icon :color="region.color" small>mdi-circle</v-icon>
                      <span class="ml-2">{{ region.name }}</span>
                      <v-spacer></v-spacer>
                      <v-btn
                        icon
                        small
                        @click="deleteRegion(region.id)"
                      >
                        <v-icon color="error" small>mdi-delete</v-icon>
                      </v-btn>
                    </v-card-title>
                    <v-card-text>
                      <v-row dense>
                        <v-col cols="6">
                          <v-text-field
                            :value="region.center.lat"
                            label="Latitude"
                            type="number"
                            step="0.0001"
                            dense
                            outlined
                            @input="updateRegionProperty(region.id, 'lat', $event)"
                          ></v-text-field>
                        </v-col>
                        <v-col cols="6">
                          <v-text-field
                            :value="region.center.lng"
                            label="Longitude"
                            type="number"
                            step="0.0001"
                            dense
                            outlined
                            @input="updateRegionProperty(region.id, 'lng', $event)"
                          ></v-text-field>
                        </v-col>
                        <v-col cols="12">
                          <v-text-field
                            :value="Math.round(region.radius / 1000)"
                            label="Radius (km)"
                            type="number"
                            step="1"
                            dense
                            outlined
                            @input="updateRegionProperty(region.id, 'radius', $event)"
                            :hint="`${region.radius.toLocaleString()} meters`"
                            persistent-hint
                          ></v-text-field>
                        </v-col>
                        <v-col cols="12">
                          <div class="text-caption grey--text">
                            Detections in region: {{ regionDetectionCounts[region.id] || 0 }}
                          </div>
                        </v-col>
                      </v-row>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
  
        <!-- Summary Statistics -->
        <v-col cols="12">
          <v-card dark>
            <v-card-title>
              Summary Statistics
              <v-spacer></v-spacer>
              <v-btn
                color="success"
                @click="exportToCSV"
                :disabled="allDetections.length === 0 && allNotInGridDetections.length === 0"
                :loading="exportingCSV"
              >
                <v-icon left>mdi-download</v-icon>
                Export CSV ({{ allDetections.length + allNotInGridDetections.length }} records)
              </v-btn>
            </v-card-title>
            <v-card-text>
              <v-row>
                <v-col cols="12" md="3">
                  <v-card outlined>
                    <v-card-text class="text-center">
                      <div class="text-h4 primary--text">{{ filteredDetections.length }}</div>
                      <div>Total Detections</div>
                    </v-card-text>
                  </v-card>
                </v-col>
                <v-col cols="12" md="3">
                  <v-card outlined>
                    <v-card-text class="text-center">
                      <div class="text-h4 success--text">{{ uniqueDevices.length }}</div>
                      <div>Active Devices</div>
                    </v-card-text>
                  </v-card>
                </v-col>
                <v-col cols="12" md="3">
                  <v-card outlined>
                    <v-card-text class="text-center">
                      <div class="text-h4 info--text">{{ averageScore.toFixed(2) }}</div>
                      <div>Average Score</div>
                    </v-card-text>
                  </v-card>
                </v-col>
                <v-col cols="12" md="3">
                  <v-card outlined>
                    <v-card-text class="text-center">
                      <div class="text-h4 warning--text">{{ peakHour }}</div>
                      <div>Peak Activity Hour</div>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
  
        <!-- Device Breakdown Table -->
        <v-col cols="12">
          <v-card dark>
            <v-card-title>
              Device Detection Breakdown
              <v-spacer></v-spacer>
              <v-text-field
                v-model="deviceSearch"
                append-icon="mdi-magnify"
                label="Search"
                single-line
                hide-details
                dense
              ></v-text-field>
            </v-card-title>
            <v-data-table
              :headers="deviceHeaders"
              :items="deviceBreakdown"
              :search="deviceSearch"
              :items-per-page="10"
              class="elevation-1"
              dense
            >
              <template v-slot:item.avgScore="{ item }">
                {{ item.avgScore.toFixed(2) }}
              </template>
            </v-data-table>
          </v-card>
        </v-col>
      </v-row>
  
      <!-- Empty State -->
      <v-row v-else-if="!querying && hasQueried && !hasFilteredData">
        <v-col cols="12">
          <v-card dark>
            <v-card-text class="text-center pa-8">
              <v-icon size="64" color="grey">mdi-bird</v-icon>
              <div class="text-h6 mt-4">No detections found</div>
              <div class="grey--text">Try adjusting your filters or date range</div>
              <div class="grey--text mt-2" v-if="totalDetections > 0">
                {{ totalDetections + allNotInGridDetections.length }} total detections filtered out by current settings
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
      </div>
    </v-container>
  </template>
  
  <script>
  import { API } from "aws-amplify";
  import {
    hitsBySpeciesByDate,
    hitNotInGridBySpeciesByDate,
    dailyCountSummaryByDateBySpecies,
    haikuboxByType
  } from "../graphql/queries";
  import moment from "moment-timezone";
  import { LMap, LTileLayer, LCircle, LPopup } from "vue2-leaflet";
  import "leaflet/dist/leaflet.css";
  import L from "leaflet";
  import "leaflet.heat";
  
  // Fix for default markers
  delete L.Icon.Default.prototype._getIconUrl;
  L.Icon.Default.mergeOptions({
    iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
    iconUrl: require("leaflet/dist/images/marker-icon.png"),
    shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
  });
  
  export default {
    name: "Science",
    props: ["user"],
    components: {
      LMap,
      LTileLayer,
      LCircle,
      LPopup
    },
    data() {
      return {
        // Filters
        selectedSpecies: null,
        selectedSpeciesData: null,
        speciesList: [],
        loadingSpecies: false,
        startDate: moment().subtract(30, 'days').format('YYYY-MM-DD'),
        endDate: moment().format('YYYY-MM-DD'),
        startDateMenu: false,
        endDateMenu: false,
        quickRange: '30d',
        minScore: 0.5,
        minDailyDetections: 1,
        
        // Query state
        querying: false,
        cancelQuery: false,
        queryProgress: 0,
        progressMessage: '',
        hasQueried: false,
        
        // Data
        allDetections: [],
        filteredDetections: [],
        allNotInGridDetections: [],
        filteredNotInGridDetections: [],
        totalDetections: 0,
        devices: {},
        devicesWithSpecies: [],
        
        // Visualization settings
        timeGrouping: 'daily',
        mapView: 'circles',
        deviceSearch: '',
        filterPanel: 0, // Expanded by default
        animationPanel: 0, // Expanded by default
        
        // Region selection
        regionSelectionMode: false,
        selectedRegions: [],
        regionColors: ['#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8', '#F7DC6F'],
        nextRegionId: 1,
        drawingRegion: null,
        regionNameDialog: false,
        newRegionName: '',
        pendingRegion: null,
        
        // Animation settings
        animating: false,
        animationInterval: null,
        currentAnimationDate: 0,
        animationSpeed: 1000, // milliseconds per frame
        dailyDetectionData: {},
        
        // Export state
        exportingCSV: false,
        
        // Map settings
        zoom: 4,
        mapCenter: [39.8283, -98.5795], // Center of USA
        urlTiles: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        heatmapLayer: null,
        heatmapOverlayCircles: [],
        animationCircles: [],
        
        // Table headers
        deviceHeaders: [
          { text: 'Device', value: 'name' },
          { text: 'Serial', value: 'serial' },
          { text: 'Detections', value: 'count' },
          { text: 'Average Score', value: 'avgScore' },
          { text: 'First Detection', value: 'firstDetection' },
          { text: 'Last Detection', value: 'lastDetection' }
        ],

        isAuthenticated: false,
        passwordDialog: false,
        password: '',
        incorrectPassword: false
      };
    },
    computed: {
      animationDates() {
        return Object.keys(this.dailyDetectionData).sort();
      },
      
      regionDetectionCounts() {
        const counts = {};
        
        this.selectedRegions.forEach(region => {
          counts[region.id] = 0;
          
          this.filteredDetections.forEach(detection => {
            const point = L.latLng(detection.lat, detection.lon);
            const regionCenter = L.latLng(region.center.lat, region.center.lng);
            const distance = point.distanceTo(regionCenter);
            
            if (distance <= region.radius) {
              counts[region.id]++;
            }
          });
        });
        
        return counts;
      },
      
      hasDataInAnyRegion() {
        if (this.selectedRegions.length === 0) return true;
        
        const counts = this.regionDetectionCounts;
        return Object.values(counts).some(count => count > 0);
      },
      
      formattedChartData() {
        // When no regions selected, return simple array format
        if (this.selectedRegions.length === 0) {
          return this.timeSeriesData;
        }
        
        // When regions selected, check if we have any data
        const seriesData = this.timeSeriesData;
        if (!seriesData || seriesData.length === 0) {
          return [];
        }
        
        // For debugging, log the data
        console.log('Time series data for regions:', seriesData);
        console.log('Number of series:', seriesData.length);
        console.log('Selected regions:', this.selectedRegions.length);
        
        // For multiple series, vue-chartkick expects an array of series objects
        // Ensure each series has data
        const validSeries = seriesData.filter(series => series.data && series.data.length > 0);
        
        if (validSeries.length === 0) {
          console.log('No valid series data found');
          return [];
        }
        
        return validSeries;
      },
      
      chartColors() {
        if (this.selectedRegions.length === 0) {
          return ['#2196F3'];
        }
        
        // Extract colors from the series data in the same order
        const colors = this.timeSeriesData.map(series => series.color || '#2196F3');
        console.log('Chart colors:', colors);
        console.log('Series names:', this.timeSeriesData.map(s => s.name));
        return colors;
      },
      
      currentDateLabel() {
        if (this.currentAnimationDate !== null && 
            this.currentAnimationDate >= 0 && 
            this.animationDates.length > 0 &&
            this.currentAnimationDate < this.animationDates.length) {
          return moment(this.animationDates[this.currentAnimationDate]).format('MMMM D, YYYY');
        }
        return '';
      },
      
      timeSeriesData() {
        if (this.filteredDetections.length === 0) {
          return this.selectedRegions.length === 0 ? [] : [];
        }
        
        // Force dependency on selectedRegions changes
        const regionsSnapshot = this.selectedRegions.map(r => ({
          id: r.id,
          lat: r.center.lat,
          lng: r.center.lng,
          radius: r.radius
        }));
        
        // If no regions selected, return single series as simple array
        if (regionsSnapshot.length === 0) {
          const grouped = {};
          
          this.filteredDetections.forEach(detection => {
            let key;
            const dt = moment(detection.dt);
            
            switch (this.timeGrouping) {
              case 'hourly':
                key = dt.format('YYYY-MM-DD HH:00');
                break;
              case 'daily':
                key = dt.format('YYYY-MM-DD');
                break;
              case 'weekly':
                key = dt.startOf('week').format('YYYY-MM-DD');
                break;
            }
            
            grouped[key] = (grouped[key] || 0) + 1;
          });
          
          // Sort by date and convert to array format
          const sortedData = Object.entries(grouped)
            .sort((a, b) => moment(a[0]).diff(moment(b[0])))
            .map(([date, count]) => [date, count]);
          
          // Return simple array for single series
          return sortedData;
        }
        
        // Multiple regions - create series for each region plus total
        const series = [];
        const allGrouped = {};
        
        console.log('Processing regions, total detections:', this.filteredDetections.length);
        console.log('Regions snapshot:', regionsSnapshot);
        
        // Process each region using the snapshot
        this.selectedRegions.forEach((region, index) => {
          const grouped = {};
          let regionDetectionCount = 0;
          
          this.filteredDetections.forEach(detection => {
            // Check if detection is within this region
            const point = L.latLng(detection.lat, detection.lon);
            const regionCenter = L.latLng(regionsSnapshot[index].lat, regionsSnapshot[index].lng);
            const distance = point.distanceTo(regionCenter);
            
            if (distance <= regionsSnapshot[index].radius) {
              regionDetectionCount++;
              let key;
              const dt = moment(detection.dt);
              
              switch (this.timeGrouping) {
                case 'hourly':
                  key = dt.format('YYYY-MM-DD HH:00');
                  break;
                case 'daily':
                  key = dt.format('YYYY-MM-DD');
                  break;
                case 'weekly':
                  key = dt.startOf('week').format('YYYY-MM-DD');
                  break;
              }
              
              grouped[key] = (grouped[key] || 0) + 1;
              allGrouped[key] = (allGrouped[key] || 0) + 1;
            }
          });
          
          console.log(`Region ${region.name}: ${regionDetectionCount} detections found`);
          
          const sortedData = Object.entries(grouped)
            .sort((a, b) => moment(a[0]).diff(moment(b[0])))
            .map(([date, count]) => [date, count]);
          
          series.push({
            name: region.name,
            data: sortedData,
            color: region.color
          });
        });
        
        // Add total series at the beginning
        const totalData = Object.entries(allGrouped)
          .sort((a, b) => moment(a[0]).diff(moment(b[0])))
          .map(([date, count]) => [date, count]);
        
        if (totalData.length > 0) {
          series.unshift({
            name: 'All Selected Regions',
            data: totalData,
            color: '#2196F3'
          });
        }
        
        console.log('Final series data:', series);
        
        // Return array of series objects for multiple series
        return series;
      },
      
      locationData() {
        const locations = {};
        
        // Process regular detections
        this.filteredDetections.forEach(detection => {
          const key = `${detection.lat}-${detection.lon}`;
          if (!locations[key]) {
            locations[key] = {
              lat: detection.lat,
              lon: detection.lon,
              count: 0,
              devices: new Set(),
              type: 'regular'
            };
          }
          locations[key].count++;
          locations[key].devices.add(detection.serial);
        });
        
        // Convert device sets to arrays for regular detections
        Object.values(locations).forEach(loc => {
          if (loc.devices instanceof Set) {
            loc.devices = Array.from(loc.devices);
          }
        });
        
        return Object.values(locations);
      },
      
      notInGridLocationData() {
        const locations = {};
        
        // Process not-in-grid detections
        this.filteredNotInGridDetections.forEach(detection => {
          const lat = detection.latitude || detection.lat;
          const lon = detection.longitude || detection.lon;
          const key = `${lat}-${lon}`;
          if (!locations[key]) {
            locations[key] = {
              lat: lat,
              lon: lon,
              count: 0,
              devices: new Set(),
              type: 'notInGrid'
            };
          }
          locations[key].count++;
          locations[key].devices.add(detection.serial);
        });
        
        // Convert device sets to arrays
        return Object.values(locations).map(loc => ({
          ...loc,
          devices: Array.from(loc.devices)
        }));
      },
      
      uniqueDevices() {
        return [...new Set(this.filteredDetections.map(d => d.serial))];
      },
      
      averageScore() {
        if (this.filteredDetections.length === 0) return 0;
        const sum = this.filteredDetections.reduce((acc, d) => acc + d.score, 0);
        return sum / this.filteredDetections.length;
      },
      
      peakHour() {
        if (this.filteredDetections.length === 0) return 'N/A';
        
        const hourCounts = {};
        this.filteredDetections.forEach(d => {
          const hour = moment(d.dt).format('HH:00');
          hourCounts[hour] = (hourCounts[hour] || 0) + 1;
        });
        
        const peakHour = Object.entries(hourCounts)
          .sort((a, b) => b[1] - a[1])[0];
        
        return peakHour ? peakHour[0] : 'N/A';
      },
      
      deviceBreakdown() {
        const breakdown = {};
        
        this.filteredDetections.forEach(detection => {
          if (!breakdown[detection.serial]) {
            breakdown[detection.serial] = {
              serial: detection.serial,
              name: this.getDeviceName(detection.serial),
              count: 0,
              scores: [],
              firstDetection: detection.dt,
              lastDetection: detection.dt
            };
          }
          
          const device = breakdown[detection.serial];
          device.count++;
          device.scores.push(detection.score);
          
          if (moment(detection.dt).isBefore(device.firstDetection)) {
            device.firstDetection = detection.dt;
          }
          if (moment(detection.dt).isAfter(device.lastDetection)) {
            device.lastDetection = detection.dt;
          }
        });
        
        // Calculate average scores and format dates
        return Object.values(breakdown).map(device => ({
          ...device,
          avgScore: device.scores.reduce((a, b) => a + b, 0) / device.scores.length,
          firstDetection: moment(device.firstDetection).format('MMM DD, YYYY HH:mm'),
          lastDetection: moment(device.lastDetection).format('MMM DD, YYYY HH:mm')
        }));
      },
      
      hasFilteredData() {
        return this.filteredDetections.length > 0 || this.filteredNotInGridDetections.length > 0;
      },
      
      chartOptions() {
        return {
          scales: {
            xAxes: [{
              type: 'time',
              time: {
                displayFormats: {
                  hour: 'MMM DD HH:mm',
                  day: 'MMM DD',
                  week: 'MMM DD'
                }
              },
              ticks: {
                fontColor: '#ffffff',
              }
            }],
            yAxes: [{
              ticks: {
                fontColor: '#ffffff',
                beginAtZero: true
              },
              scaleLabel: {
                display: true,
                labelString: 'Number of Detections',
                fontColor: '#ffffff'
              }
            }]
          },
          legend: {
            display: this.selectedRegions.length > 0,
            labels: {
              fontColor: '#ffffff'
            }
          }
        };
      }
    },
    watch: {
      selectedRegions: {
        deep: true,
        handler() {
          // Force update of computed properties that depend on regions
          this.$nextTick(() => {
            // Trigger re-computation of time series data
            if (this.hasQueried && this.hasFilteredData) {
              // Force Vue to update the chart by triggering a re-render
              this.$forceUpdate();
            }
          });
        }
      },
      
      selectedSpecies(val) {
        if (val) {
          this.selectedSpeciesData = this.speciesList.find(s => s.cn === val);
        } else {
          this.selectedSpeciesData = null;
        }
      },
      
      animationSpeed() {
        // Restart animation with new speed if currently animating
        if (this.animating) {
          this.stopAnimation();
          this.startAnimation();
        }
      },
      
      quickRange(val) {
        const now = moment();
        this.endDate = now.format('YYYY-MM-DD');
        
        switch(val) {
          case '7d':
            this.startDate = now.subtract(7, 'days').format('YYYY-MM-DD');
            break;
          case '30d':
            this.startDate = now.subtract(30, 'days').format('YYYY-MM-DD');
            break;
          case '90d':
            this.startDate = now.subtract(90, 'days').format('YYYY-MM-DD');
            break;
        }
      },
      
      minScore() {
        // Update animation data when score filter changes
        if (this.mapView === 'animated' && this.animationDates.length > 0) {
          this.prepareAnimationData();
        }
      },
      
      minDailyDetections() {
        // Update animation frame when daily detection filter changes
        if (this.mapView === 'animated' && this.animationDates.length > 0) {
          this.updateAnimationFrame();
        }
      },
      
      mapView(val) {
        // Always clean up previous view's elements first
        if (this.heatmapLayer && this.$refs.detectionMap) {
          this.$refs.detectionMap.mapObject.removeLayer(this.heatmapLayer);
          this.heatmapLayer = null;
        }
        
        // Remove heatmap overlay circles
        this.heatmapOverlayCircles.forEach(circle => {
          if (this.$refs.detectionMap) {
            this.$refs.detectionMap.mapObject.removeLayer(circle);
          }
        });
        this.heatmapOverlayCircles = [];
        
        // Remove animation circles
        this.animationCircles.forEach(circle => {
          if (this.$refs.detectionMap) {
            this.$refs.detectionMap.mapObject.removeLayer(circle);
          }
        });
        this.animationCircles = [];
        
        // Keep regions but exit selection mode
        this.regionSelectionMode = false;
        
        // Stop animation if running
        if (this.animating) {
          this.stopAnimation();
        }
        
        // Now set up the new view
        if (val === 'heatmap') {
          this.$nextTick(() => {
            this.initHeatmap();
          });
        } else if (val === 'animated') {
          this.$nextTick(() => {
            this.prepareAnimationData();
          });
        }
      }
    },
    async mounted() {
      setTimeout(() => {
      this.passwordDialog = true;
    }, 100);
      await this.loadDevices();
      await this.loadSpeciesList();
    },
    beforeDestroy() {
      // Clean up heatmap layer if it exists
      if (this.heatmapLayer && this.$refs.detectionMap) {
        this.$refs.detectionMap.mapObject.removeLayer(this.heatmapLayer);
      }
      
      // Clean up heatmap overlay circles
      this.heatmapOverlayCircles.forEach(circle => {
        if (this.$refs.detectionMap) {
          this.$refs.detectionMap.mapObject.removeLayer(circle);
        }
      });
      
      // Clean up animation circles
      this.animationCircles.forEach(circle => {
        if (this.$refs.detectionMap) {
          this.$refs.detectionMap.mapObject.removeLayer(circle);
        }
      });
      
      // Stop animation if running
      if (this.animationInterval) {
        clearInterval(this.animationInterval);
      }
    },
    methods: {
        checkPassword() {
            // Set your password here
            if (this.password === 'blinded') {
            this.isAuthenticated = true;
            this.passwordDialog = false;
            this.incorrectPassword = false;
            } else {
            this.incorrectPassword = true;
            }
        },
      async loadDevices() {
        try {
          let nextToken = null;
          const deviceList = [];
          
          do {
            const response = await API.graphql({
              query: haikuboxByType,
              variables: { 
                type: "haikubox", 
                nextToken: nextToken 
              }
            });
            
            deviceList.push(...response.data.haikuboxByType.items);
            nextToken = response.data.haikuboxByType.nextToken;
          } while (nextToken);
          
          // Create lookup map for devices
          deviceList.forEach(device => {
            this.devices[device.serial] = device;
          });
          
        } catch (error) {
          console.error("Error loading devices:", error);
        }
      },
      
      async loadSpeciesList() {
        this.loadingSpecies = true;
        try {
          // Load species list from static JSON file
          // Format: array of arrays [spCode, commonName, scientificName, val1, val2]
          const response = await fetch('/species-list.json');
          const speciesData = await response.json();
          
          this.speciesList = speciesData.map(species => ({
            spCode: species[0],
            cn: species[1],
            sn: species[2],
            display: `${species[1]} (${species[2]})`
          })).sort((a, b) => a.cn.localeCompare(b.cn));
          
          console.log(`Loaded ${this.speciesList.length} species`);
          
        } catch (error) {
          console.error("Error loading species list:", error);
          // Fallback to empty list if file not found
          this.speciesList = [];
        } finally {
          this.loadingSpecies = false;
        }
      },
      
      async queryData() {
        if (!this.selectedSpecies || !this.startDate || !this.endDate) return;
        
        this.querying = true;
        this.cancelQuery = false;
        this.queryProgress = 0;
        this.hasQueried = true;
        this.allDetections = [];
        this.filteredDetections = [];
        this.allNotInGridDetections = [];
        this.filteredNotInGridDetections = [];
        this.devicesWithSpecies = [];
        this.dailyDetectionData = {};
        this.currentAnimationDate = 0;
        
        // Clean up any existing animation layers
        if (this.heatmapLayer && this.$refs.detectionMap) {
          this.$refs.detectionMap.mapObject.removeLayer(this.heatmapLayer);
          this.heatmapLayer = null;
        }
        
        this.heatmapOverlayCircles.forEach(circle => {
          if (this.$refs.detectionMap) {
            this.$refs.detectionMap.mapObject.removeLayer(circle);
          }
        });
        this.heatmapOverlayCircles = [];
        
        this.animationCircles.forEach(circle => {
          if (this.$refs.detectionMap) {
            this.$refs.detectionMap.mapObject.removeLayer(circle);
          }
        });
        this.animationCircles = [];
        
        // Stop any running animation
        if (this.animating) {
          this.stopAnimation();
        }
        
        try {
          // Step 1: Find devices that have recorded this species
          this.progressMessage = 'Finding devices with this species...';
          await this.findDevicesWithSpecies();
          
          if (this.cancelQuery) {
            this.progressMessage = 'Query cancelled';
            return;
          }
          
          if (this.devicesWithSpecies.length === 0) {
            this.querying = false;
            return;
          }
          
          // Step 2: Query hit data for each device
          const totalDevices = this.devicesWithSpecies.length;
          let processedDevices = 0;
          
          for (const serial of this.devicesWithSpecies) {
            if (this.cancelQuery) {
              this.progressMessage = 'Query cancelled';
              break;
            }
            
            this.progressMessage = `Querying device ${processedDevices + 1} of ${totalDevices}`;
            
            await this.queryDeviceHits(serial);
            
            processedDevices++;
            this.queryProgress = (processedDevices / totalDevices) * 100;
          }
          
          if (!this.cancelQuery) {
            this.totalDetections = this.allDetections.length;
            
            // Step 3: Query not-in-grid detections
            this.progressMessage = 'Querying unexpected location detections...';
            await this.queryNotInGridDetections();
            
            this.filterDataByScore();
            
            // Center map on detections if any found
            if (this.filteredDetections.length > 0 || this.filteredNotInGridDetections.length > 0) {
              this.centerMapOnDetections();
            }
            
            // Prepare animation data if animated view is selected
            if (this.mapView === 'animated') {
              this.prepareAnimationData();
            }
          }
          
        } catch (error) {
          console.error("Error querying data:", error);
          this.$emit('error', 'Failed to query detection data');
        } finally {
          this.querying = false;
          if (!this.cancelQuery) {
            this.progressMessage = '';
          }
        }
      },
      
      async findDevicesWithSpecies() {
        // First try using daily summaries for more accurate results
        const deviceSet = new Set();
        
        // Sample a few dates throughout the range to find active devices
        const startMoment = moment(this.startDate);
        const endMoment = moment(this.endDate);
        const daysDiff = endMoment.diff(startMoment, 'days');
        
        // Sample every 7 days or at least 5 samples
        const sampleInterval = Math.max(1, Math.floor(daysDiff / 5));
        let currentDate = moment(this.startDate);
        
        this.progressMessage = `Scanning for devices with ${this.selectedSpecies}...`;
        
        while (currentDate.isSameOrBefore(endMoment)) {
          if (this.cancelQuery) break;
          
          let nextToken = null;
          
          do {
            if (this.cancelQuery) break;
            
            const response = await API.graphql({
              query: dailyCountSummaryByDateBySpecies,
              variables: {
                countDate: currentDate.format('YYYY-MM-DD'),
                cn: {
                  eq: this.selectedSpecies
                },
                nextToken: nextToken
              }
            });
            
            response.data.dailyCountSummaryByDateBySpecies.items.forEach(item => {
              if (item.serial) {
                deviceSet.add(item.serial);
              }
            });
            
            nextToken = response.data.dailyCountSummaryByDateBySpecies.nextToken;
          } while (nextToken);
          
          currentDate.add(sampleInterval, 'days');
        }
        
        this.devicesWithSpecies = Array.from(deviceSet);
        console.log(`Found ${this.devicesWithSpecies.length} devices with ${this.selectedSpecies}`);
      },
      
      async queryDeviceHits(serial) {
        const startDt = moment(this.startDate).utc().format();
        const endDt = moment(this.endDate).add(1, 'day').utc().format();
        
        let nextToken = null;
        
        do {
          if (this.cancelQuery) break;
          
          try {
            const response = await API.graphql({
              query: hitsBySpeciesByDate,
              variables: {
                serial: serial,
                cnDt: {
                  between: [
                    { cn: this.selectedSpecies, dt: startDt },
                    { cn: this.selectedSpecies, dt: endDt }
                  ]
                },
                nextToken: nextToken,
                sortDirection: "DESC",
                limit: 1000
              }
            });
            
            const hits = response.data.hitsBySpeciesByDate.items
              .filter(hit => hit.score !== undefined);
            
            this.allDetections = this.allDetections.concat(hits);
            nextToken = response.data.hitsBySpeciesByDate.nextToken;
            
          } catch (error) {
            console.error(`Error querying device ${serial}:`, error);
            console.log(`Attempted query with cn: ${this.selectedSpecies}, dates: ${startDt} to ${endDt}`);
            // Continue with next device even if this one fails
            break;
          }
        } while (nextToken);
      },
      
      async queryNotInGridDetections() {
        const startDt = moment(this.startDate).utc().format();
        const endDt = moment(this.endDate).add(1, 'day').utc().format();
        
        let nextToken = null;
        
        do {
          if (this.cancelQuery) break;
          
          try {
            const response = await API.graphql({
              query: hitNotInGridBySpeciesByDate,
              variables: {
                spCode: this.selectedSpeciesData ? this.selectedSpeciesData.spCode : '',
                dt: { between: [startDt, endDt] },
                nextToken: nextToken,
                sortDirection: "DESC",
                limit: 1000
              }
            });
            
            const hits = response.data.hitNotInGridBySpeciesByDate.items
              .filter(hit => hit.score !== undefined);
            
            this.allNotInGridDetections = this.allNotInGridDetections.concat(hits);
            nextToken = response.data.hitNotInGridBySpeciesByDate.nextToken;
            
          } catch (error) {
            console.error('Error querying not-in-grid detections:', error);
            // Continue even if this query fails
            break;
          }
        } while (nextToken);
        
        console.log(`Found ${this.allNotInGridDetections.length} not-in-grid detections`);
      },
      
      filterDataByScore() {
        // First filter by score
        const scoreFilteredDetections = this.allDetections.filter(d => d.score >= this.minScore);
        const scoreFilteredNotInGrid = this.allNotInGridDetections.filter(d => d.score >= this.minScore);
        
        // Group by date and location to apply daily detection minimum
        if (this.minDailyDetections > 1) {
          // Process regular detections
          const dailyGroups = {};
          scoreFilteredDetections.forEach(detection => {
            const date = moment(detection.dt).format('YYYY-MM-DD');
            const key = `${date}-${detection.lat}-${detection.lon}`;
            
            if (!dailyGroups[key]) {
              dailyGroups[key] = [];
            }
            dailyGroups[key].push(detection);
          });
          
          // Filter out locations with less than minimum daily detections
          this.filteredDetections = [];
          Object.values(dailyGroups).forEach(group => {
            if (group.length >= this.minDailyDetections) {
              this.filteredDetections.push(...group);
            }
          });
          
          // Process not-in-grid detections
          const dailyNotInGridGroups = {};
          scoreFilteredNotInGrid.forEach(detection => {
            const lat = detection.latitude || detection.lat;
            const lon = detection.longitude || detection.lon;
            const date = moment(detection.dt).format('YYYY-MM-DD');
            const key = `${date}-${lat}-${lon}`;
            
            if (!dailyNotInGridGroups[key]) {
              dailyNotInGridGroups[key] = [];
            }
            dailyNotInGridGroups[key].push(detection);
          });
          
          // Filter out locations with less than minimum daily detections
          this.filteredNotInGridDetections = [];
          Object.values(dailyNotInGridGroups).forEach(group => {
            if (group.length >= this.minDailyDetections) {
              this.filteredNotInGridDetections.push(...group);
            }
          });
        } else {
          // If minimum is 1, just use score-filtered results
          this.filteredDetections = scoreFilteredDetections;
          this.filteredNotInGridDetections = scoreFilteredNotInGrid;
        }
        
        // Update animation data if in animated view
        if (this.mapView === 'animated') {
          this.prepareAnimationData();
        }
      },
      
      getDeviceName(serial) {
        return this.devices[serial]?.name || serial;
      },
      
      getCircleColor() {
        // Always return blue for regular detections
        return '#2196F3';
      },
      
      getNotInGridColor() {
        // Always return red for not-in-grid detections
        return '#f44336';
      },
      
      getCircleRadius(count, type = 'regular') {
        // Scale radius based on count, with min/max bounds
        const minRadius = type === 'regular' ? 5000 : 3000; // Smaller for not-in-grid
        const maxRadius = type === 'regular' ? 50000 : 10000; // Smaller max for not-in-grid
        
        // Get max count based on type
        const maxCount = type === 'regular' 
          ? Math.max(...this.locationData.map(l => l.count), 1)
          : Math.max(...this.notInGridLocationData.map(l => l.count), 1);
        
        return minRadius + (count / maxCount) * (maxRadius - minRadius);
      },
      
      centerMapOnDetections() {
        const allLocations = [...this.locationData, ...this.notInGridLocationData];
        
        if (allLocations.length === 0) return;
        
        const lats = allLocations.map(l => l.lat);
        const lons = allLocations.map(l => l.lon);
        
        const bounds = [
          [Math.min(...lats), Math.min(...lons)],
          [Math.max(...lats), Math.max(...lons)]
        ];
        
        this.$nextTick(() => {
          if (this.$refs.detectionMap) {
            this.$refs.detectionMap.mapObject.fitBounds(bounds, { padding: [50, 50] });
            
            // Initialize visualization based on selected view
            if (this.mapView === 'heatmap') {
              setTimeout(() => {
                this.initHeatmap();
              }, 500);
            } else if (this.mapView === 'animated') {
              setTimeout(() => {
                this.prepareAnimationData();
              }, 500);
            }
          }
        });
      },
      
      initHeatmap() {
        // Remove existing heatmap layer if any
        if (this.heatmapLayer && this.$refs.detectionMap) {
          this.$refs.detectionMap.mapObject.removeLayer(this.heatmapLayer);
        }
        
        // Combine both regular and not-in-grid data for heatmap
        const allLocations = [...this.locationData, ...this.notInGridLocationData];
        
        // Prepare heat data: [lat, lng, intensity]
        const heatData = allLocations.map(loc => {
          // Normalize intensity based on max count across all data
          const maxCount = Math.max(...allLocations.map(l => l.count), 1);
          const intensity = loc.count / maxCount;
          return [loc.lat, loc.lon, intensity];
        });
        
        // Create heatmap layer with options
        if (this.$refs.detectionMap && heatData.length > 0) {
          this.heatmapLayer = L.heatLayer(heatData, {
            radius: 25,
            blur: 15,
            maxZoom: 17,
            max: 1.0,
            gradient: {
              0.0: 'blue',
              0.25: 'cyan',
              0.5: 'lime',
              0.75: 'yellow',
              1.0: 'red'
            }
          }).addTo(this.$refs.detectionMap.mapObject);
          
          console.log(`Heatmap created with ${heatData.length} points`);
        }
        
        // Add circle overlays for both types to distinguish them
        this.addCircleOverlays();
      },
      
      addCircleOverlays() {
        // Remove any existing overlay circles first
        this.heatmapOverlayCircles.forEach(circle => {
          if (this.$refs.detectionMap) {
            this.$refs.detectionMap.mapObject.removeLayer(circle);
          }
        });
        this.heatmapOverlayCircles = [];
        
        // Add semi-transparent circles on top of heatmap
        // Regular detections (blue)
        this.locationData.forEach(location => {
          const circle = L.circle([location.lat, location.lon], {
            radius: this.getCircleRadius(location.count, 'regular'),
            color: '#2196F3',
            weight: 2,
            fillColor: '#2196F3',
            fillOpacity: 0.3
          }).addTo(this.$refs.detectionMap.mapObject).bindPopup(`
            <div>
              <strong>${this.selectedSpecies}</strong><br>
              <em>${this.selectedSpeciesData ? this.selectedSpeciesData.sn : ''}</em><br>
              Code: ${this.selectedSpeciesData ? this.selectedSpeciesData.spCode : ''}<br>
              <strong style="color: #2196F3">Regular Detection</strong><br>
              Detections: ${location.count}<br>
              Location: ${location.lat.toFixed(4)}, ${location.lon.toFixed(4)}<br>
              Devices: ${location.devices.join(', ')}
            </div>
          `);
          this.heatmapOverlayCircles.push(circle);
        });
        
        // Not-in-grid detections (red)
        this.notInGridLocationData.forEach(location => {
          const circle = L.circle([location.lat, location.lon], {
            radius: this.getCircleRadius(location.count, 'notInGrid'),
            color: '#f44336',
            weight: 2,
            fillColor: '#f44336',
            fillOpacity: 0.3
          }).addTo(this.$refs.detectionMap.mapObject).bindPopup(`
            <div>
              <strong>${this.selectedSpecies}</strong><br>
              <em>${this.selectedSpeciesData ? this.selectedSpeciesData.sn : ''}</em><br>
              Code: ${this.selectedSpeciesData ? this.selectedSpeciesData.spCode : ''}<br>
              <strong style="color: #f44336">Not-In-Grid Detection</strong><br>
              Detections: ${location.count}<br>
              Location: ${location.lat.toFixed(4)}, ${location.lon.toFixed(4)}<br>
              Devices: ${location.devices.join(', ')}
            </div>
          `);
          this.heatmapOverlayCircles.push(circle);
        });
      },
      
      customSpeciesFilter(item, queryText) {
        const query = queryText.toLowerCase();
        return item.cn.toLowerCase().includes(query) ||
               item.sn.toLowerCase().includes(query) ||
               item.spCode.toLowerCase().includes(query);
      },
      
      stopQuery() {
        this.cancelQuery = true;
        this.progressMessage = 'Cancelling query...';
      },
      
      prepareAnimationData() {
        // Clear any existing animation layers
        if (this.heatmapLayer && this.$refs.detectionMap) {
          this.$refs.detectionMap.mapObject.removeLayer(this.heatmapLayer);
          this.heatmapLayer = null;
        }
        
        // Remove heatmap overlay circles
        this.heatmapOverlayCircles.forEach(circle => {
          if (this.$refs.detectionMap) {
            this.$refs.detectionMap.mapObject.removeLayer(circle);
          }
        });
        this.heatmapOverlayCircles = [];
        
        this.animationCircles.forEach(circle => {
          if (this.$refs.detectionMap) {
            this.$refs.detectionMap.mapObject.removeLayer(circle);
          }
        });
        this.animationCircles = [];
        
        // Group detections by date - using only score-filtered data
        this.dailyDetectionData = {};
        
        // Regular detections - filter by score first
        const scoreFilteredRegular = this.allDetections.filter(d => d.score >= this.minScore);
        scoreFilteredRegular.forEach(detection => {
          const date = moment(detection.dt).format('YYYY-MM-DD');
          if (!this.dailyDetectionData[date]) {
            this.dailyDetectionData[date] = {
              regular: [],
              notInGrid: []
            };
          }
          this.dailyDetectionData[date].regular.push(detection);
        });
        
        // Not-in-grid detections - filter by score first
        const scoreFilteredNotInGrid = this.allNotInGridDetections.filter(d => d.score >= this.minScore);
        scoreFilteredNotInGrid.forEach(detection => {
          const date = moment(detection.dt).format('YYYY-MM-DD');
          if (!this.dailyDetectionData[date]) {
            this.dailyDetectionData[date] = {
              regular: [],
              notInGrid: []
            };
          }
          this.dailyDetectionData[date].notInGrid.push(detection);
        });
        
        // Initialize animation
        if (this.animationDates.length > 0) {
          this.currentAnimationDate = 0;
          this.updateAnimationFrame();
        }
      },
      
      toggleAnimation() {
        if (this.animating) {
          this.stopAnimation();
        } else {
          this.startAnimation();
        }
      },
      
      startAnimation() {
        if (this.animationDates.length === 0) return;
        
        this.animating = true;
        this.animationInterval = setInterval(() => {
          this.currentAnimationDate++;
          
          // Loop back to start
          if (this.currentAnimationDate >= this.animationDates.length) {
            this.currentAnimationDate = 0;
          }
          
          this.updateAnimationFrame();
        }, this.animationSpeed);
      },
      
      stopAnimation() {
        this.animating = false;
        if (this.animationInterval) {
          clearInterval(this.animationInterval);
          this.animationInterval = null;
        }
        // Keep the last frame visible when stopping
      },
      
      updateAnimationFrame() {
        if (this.currentAnimationDate === null || this.animationDates.length === 0) return;
        
        const currentDate = this.animationDates[this.currentAnimationDate];
        const dayData = this.dailyDetectionData[currentDate] || { regular: [], notInGrid: [] };
        
        // Process regular detections
        const regularLocations = {};
        dayData.regular.forEach(detection => {
          const key = `${detection.lat}-${detection.lon}`;
          if (!regularLocations[key]) {
            regularLocations[key] = {
              lat: detection.lat,
              lon: detection.lon,
              count: 0,
              devices: new Set()
            };
          }
          regularLocations[key].count++;
          regularLocations[key].devices.add(detection.serial);
        });
        
        // Process not-in-grid detections
        const notInGridLocations = {};
        dayData.notInGrid.forEach(detection => {
          const lat = detection.latitude || detection.lat;
          const lon = detection.longitude || detection.lon;
          const key = `${lat}-${lon}`;
          if (!notInGridLocations[key]) {
            notInGridLocations[key] = {
              lat: lat,
              lon: lon,
              count: 0,
              devices: new Set()
            };
          }
          notInGridLocations[key].count++;
          notInGridLocations[key].devices.add(detection.serial);
        });
        
        // Filter by minimum daily detections
        const regularArray = Object.values(regularLocations)
          .filter(loc => loc.count >= this.minDailyDetections)
          .map(loc => ({
            ...loc,
            devices: Array.from(loc.devices)
          }));
        
        const notInGridArray = Object.values(notInGridLocations)
          .filter(loc => loc.count >= this.minDailyDetections)
          .map(loc => ({
            ...loc,
            devices: Array.from(loc.devices)
          }));
        
        // Remove existing layers
        if (this.heatmapLayer && this.$refs.detectionMap) {
          this.$refs.detectionMap.mapObject.removeLayer(this.heatmapLayer);
        }
        
        this.animationCircles.forEach(circle => {
          if (this.$refs.detectionMap) {
            this.$refs.detectionMap.mapObject.removeLayer(circle);
          }
        });
        this.animationCircles = [];
        
        // Create combined heatmap
        const allLocations = [...regularArray, ...notInGridArray];
        
        if (allLocations.length > 0 && this.$refs.detectionMap) {
          // Get max counts for consistent scaling across all dates
          const allDayMaxCount = Math.max(
            ...Object.values(this.dailyDetectionData).flatMap(dayData => {
              const dayCounts = {};
              
              // Process regular detections
              dayData.regular.forEach(d => {
                const key = `${d.lat}-${d.lon}`;
                dayCounts[key] = (dayCounts[key] || 0) + 1;
              });
              
              // Process not-in-grid detections
              dayData.notInGrid.forEach(d => {
                const lat = d.latitude || d.lat;
                const lon = d.longitude || d.lon;
                const key = `${lat}-${lon}`;
                dayCounts[key] = (dayCounts[key] || 0) + 1;
              });
              
              return Object.values(dayCounts);
            }),
            1
          );
          
          // Create heatmap
          const heatData = allLocations.map(loc => [
            loc.lat,
            loc.lon,
            loc.count / allDayMaxCount
          ]);
          
          this.heatmapLayer = L.heatLayer(heatData, {
            radius: 25,
            blur: 15,
            maxZoom: 17,
            max: 1.0,
            gradient: {
              0.0: 'blue',
              0.25: 'cyan',
              0.5: 'lime',
              0.75: 'yellow',
              1.0: 'red'
            }
          }).addTo(this.$refs.detectionMap.mapObject);
          
          // Add regular detection circles (blue)
          regularArray.forEach(location => {
            const circle = L.circle([location.lat, location.lon], {
              radius: this.getCircleRadius(location.count, 'regular'),
              color: '#2196F3',
              weight: 2,
              fillColor: '#2196F3',
              fillOpacity: 0.3
            }).addTo(this.$refs.detectionMap.mapObject);
            
            circle.bindPopup(`
              <div>
                <strong>${this.selectedSpecies}</strong><br>
                <em>${this.selectedSpeciesData ? this.selectedSpeciesData.sn : ''}</em><br>
                <strong style="color: #2196F3">Regular Detection</strong><br>
                Date: ${moment(currentDate).format('MMMM D, YYYY')}<br>
                Detections: ${location.count}<br>
                Location: ${location.lat.toFixed(4)}, ${location.lon.toFixed(4)}<br>
                Devices: ${location.devices.join(', ')}
              </div>
            `);
            
            this.animationCircles.push(circle);
          });
          
          // Add not-in-grid detection circles (red)
          notInGridArray.forEach(location => {
            const circle = L.circle([location.lat, location.lon], {
              radius: this.getCircleRadius(location.count, 'notInGrid'),
              color: '#f44336',
              weight: 2,
              fillColor: '#f44336',
              fillOpacity: 0.5
            }).addTo(this.$refs.detectionMap.mapObject);
            
            circle.bindPopup(`
              <div>
                <strong>${this.selectedSpecies}</strong><br>
                <em>${this.selectedSpeciesData ? this.selectedSpeciesData.sn : ''}</em><br>
                <strong style="color: #f44336">Not-In-Grid Detection</strong><br>
                Date: ${moment(currentDate).format('MMMM D, YYYY')}<br>
                Detections: ${location.count}<br>
                Location: ${location.lat.toFixed(4)}, ${location.lon.toFixed(4)}<br>
                Devices: ${location.devices.join(', ')}
              </div>
            `);
            
            this.animationCircles.push(circle);
          });
        }
      },
      
      exportToCSV() {
        this.exportingCSV = true;
        
        try {
          // CSV header
          let csvRows = 'Device Name,Device Serial,Species,Common Name,spCode,Date,Latitude,Longitude,Total Count,Count 0.2-1,Count 0.4-1,Count 0.5-1,Count 0.6-1,Count 0.8-1,Count 0.9-1,Hit in Grid\n';
          
          // Process regular detections (in grid) - use all detections, not filtered
          const regularGrouped = {};
          this.allDetections.forEach(detection => {
            const date = moment(detection.dt).format('YYYY-MM-DD');
            const key = `${detection.serial}-${date}-${detection.lat}-${detection.lon}`;
            
            if (!regularGrouped[key]) {
              regularGrouped[key] = {
                deviceName: this.getDeviceName(detection.serial),
                deviceSerial: detection.serial,
                species: this.selectedSpeciesData ? this.selectedSpeciesData.sn : '',
                commonName: this.selectedSpecies,
                spCode: this.selectedSpeciesData ? this.selectedSpeciesData.spCode : '',
                date: date,
                latitude: detection.lat.toFixed(6),
                longitude: detection.lon.toFixed(6),
                scores: [],
                hitInGrid: 'Y'
              };
            }
            regularGrouped[key].scores.push(detection.score);
          });
          
          // Process not-in-grid detections - use all detections, not filtered
          const notInGridGrouped = {};
          this.allNotInGridDetections.forEach(detection => {
            const lat = detection.latitude || detection.lat;
            const lon = detection.longitude || detection.lon;
            const date = moment(detection.dt).format('YYYY-MM-DD');
            const key = `${detection.serial}-${date}-${lat}-${lon}`;
            
            if (!notInGridGrouped[key]) {
              notInGridGrouped[key] = {
                deviceName: this.getDeviceName(detection.serial),
                deviceSerial: detection.serial,
                species: this.selectedSpeciesData ? this.selectedSpeciesData.sn : '',
                commonName: this.selectedSpecies,
                spCode: this.selectedSpeciesData ? this.selectedSpeciesData.spCode : '',
                date: date,
                latitude: lat.toFixed(6),
                longitude: lon.toFixed(6),
                scores: [],
                hitInGrid: 'N'
              };
            }
            notInGridGrouped[key].scores.push(detection.score);
          });
          
          // Combine all groups
          const allGroups = { ...regularGrouped, ...notInGridGrouped };
          
          // Convert to array and sort
          const sortedGroups = Object.values(allGroups).sort((a, b) => {
            const dateCompare = a.date.localeCompare(b.date);
            if (dateCompare !== 0) return dateCompare;
            const deviceCompare = a.deviceSerial.localeCompare(b.deviceSerial);
            if (deviceCompare !== 0) return deviceCompare;
            const latCompare = parseFloat(a.latitude) - parseFloat(b.latitude);
            if (latCompare !== 0) return latCompare;
            return parseFloat(a.longitude) - parseFloat(b.longitude);
          });
          
          // Build CSV rows
          sortedGroups.forEach(group => {
            // Calculate counts for each score bin
            const totalCount = group.scores.length;
            const count02 = group.scores.filter(s => s >= 0.2).length;
            const count04 = group.scores.filter(s => s >= 0.4).length;
            const count05 = group.scores.filter(s => s >= 0.5).length;
            const count06 = group.scores.filter(s => s >= 0.6).length;
            const count08 = group.scores.filter(s => s >= 0.8).length;
            const count09 = group.scores.filter(s => s >= 0.9).length;
            
            // Escape fields that might contain commas
            const deviceName = group.deviceName.includes(',') ? `"${group.deviceName}"` : group.deviceName;
            const species = group.species.includes(',') ? `"${group.species}"` : group.species;
            const commonName = group.commonName.includes(',') ? `"${group.commonName}"` : group.commonName;
            
            csvRows += `${deviceName},${group.deviceSerial},${species},${commonName},${group.spCode},${group.date},${group.latitude},${group.longitude},${totalCount},${count02},${count04},${count05},${count06},${count08},${count09},${group.hitInGrid}\n`;
          });
          
          // Create blob and download
          const blob = new Blob([csvRows], { type: 'text/csv' });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          
          // Generate filename with species and date range
          const filename = `bird_detections_${this.selectedSpeciesData ? this.selectedSpeciesData.spCode : 'unknown'}_${this.startDate}_to_${this.endDate}.csv`;
          
          a.setAttribute('href', url);
          a.setAttribute('download', filename);
          a.click();
          
          // Clean up
          window.URL.revokeObjectURL(url);
          
          // Show success message
          this.$emit('success', `Exported ${sortedGroups.length} rows to CSV`);
          
        } catch (error) {
          console.error('Error exporting CSV:', error);
          this.$emit('error', 'Failed to export CSV');
        } finally {
          this.exportingCSV = false;
        }
      },
      
      toggleRegionSelection() {
        this.regionSelectionMode = !this.regionSelectionMode;
        if (!this.regionSelectionMode && this.drawingRegion) {
          // Cancel any in-progress drawing
          if (this.drawingRegion.tempCircle) {
            this.$refs.detectionMap.mapObject.removeLayer(this.drawingRegion.tempCircle);
          }
          this.drawingRegion = null;
        }
      },
      
      clearRegions() {
        this.selectedRegions = [];
        this.nextRegionId = 1;
      },
      
      onMapClick(event) {
        if (!this.regionSelectionMode || this.mapView !== 'circles') return;
        
        const latlng = event.latlng;
        
        if (!this.drawingRegion) {
          // First click - set center
          const color = this.regionColors[(this.nextRegionId - 1) % this.regionColors.length];
          
          this.drawingRegion = {
            center: latlng,
            color: color,
            tempCircle: L.circle(latlng, {
              radius: 50000, // Start with 50km radius
              color: color,
              weight: 3,
              fillColor: color,
              fillOpacity: 0.2,
              dashArray: '5, 5'
            }).addTo(this.$refs.detectionMap.mapObject)
          };
          
          // Add mouse move listener to show radius preview
          this.$refs.detectionMap.mapObject.on('mousemove', this.onMouseMove);
        } else {
          // Second click - finalize region
          const center = this.drawingRegion.center;
          const radius = center.distanceTo(latlng);
          const color = this.drawingRegion.color;
          
          // Remove temporary circle
          this.$refs.detectionMap.mapObject.removeLayer(this.drawingRegion.tempCircle);
          this.$refs.detectionMap.mapObject.off('mousemove', this.onMouseMove);
          
          // Store region
          const regionName = `Region ${this.nextRegionId}`;
          this.selectedRegions.push({
            id: this.nextRegionId,
            name: regionName,
            center: { lat: center.lat, lng: center.lng },
            radius: radius,
            color: color
          });
          
          this.nextRegionId++;
          this.drawingRegion = null;
        }
      },
      
      onMouseMove(event) {
        if (this.drawingRegion && this.drawingRegion.tempCircle) {
          const radius = this.drawingRegion.center.distanceTo(event.latlng);
          this.drawingRegion.tempCircle.setRadius(radius);
        }
      },
      
      deleteRegion(regionId) {
        this.selectedRegions = this.selectedRegions.filter(r => r.id !== regionId);
      },
      
      updateRegionProperty(regionId, property, value) {
        // Find the region index
        const regionIndex = this.selectedRegions.findIndex(r => r.id === regionId);
        if (regionIndex === -1) return;
        
        // Convert value to number
        const numValue = parseFloat(value);
        if (isNaN(numValue)) return;
        
        // Create a new region object to ensure reactivity
        const updatedRegion = { ...this.selectedRegions[regionIndex] };
        
        if (property === 'lat' || property === 'lng') {
          updatedRegion.center = { ...updatedRegion.center };
          updatedRegion.center[property] = numValue;
        } else if (property === 'radius') {
          // Convert km to meters for storage
          updatedRegion.radius = numValue * 1000;
        }
        
        // Replace the entire region object to trigger reactivity
        this.$set(this.selectedRegions, regionIndex, updatedRegion);
        
        // Force chart update by triggering key change
        this.$nextTick(() => {
          // The watcher will handle the update
        });
      }
    }
  };
  </script>
  
  <style scoped>
  .v-card {
    margin-bottom: 16px;
  }
  
  .v-data-table {
    background-color: transparent !important;
  }
  
  ::v-deep .leaflet-container {
    background-color: #1e1e1e;
  }
  
  ::v-deep .v-slider__thumb-label {
    font-size: 12px;
  }
  
  .date-overlay {
    position: absolute;
    top: 10px;
    right: 10px;
    z-index: 1000;
    pointer-events: none;
  }
  
  .region-instructions {
    position: absolute;
    top: 10px;
    left: 10px;
    z-index: 1000;
    max-width: 300px;
  }
  
  .regions-legend {
    position: absolute;
    bottom: 10px;
    left: 10px;
    z-index: 1000;
    max-width: 180px;
  }
  
  .regions-legend .v-card {
    opacity: 0.9;
  }
  
  ::v-deep .v-expansion-panel-content__wrap {
    padding-top: 0;
  }
  </style>